import useAuthenticatedApi from '../../../Authentication/components/hooks/useAuthenticatedApi';

/*
 * Gallery moderation magic link — stored, revocable token (unlike the
 * client report's stateless HMAC: this link carries write power, so
 * revocation is the point). Endpoints are activation-permission
 * gated server-side.
 */

export interface ModerationToken {
    id: number;
    gallery_id: number;
    token: string;
}

export default function useModerationToken() {
    const authenticatedApi = useAuthenticatedApi();

    /* null = no active token (404) or fetch failure — the row just
       offers Create in either case */
    const getModerationToken = async (
        galleryId: number,
    ): Promise<ModerationToken | null> => {
        try {
            const response = await authenticatedApi.get(
                `/galleries/moderation/${galleryId}/token`,
            );
            return response.data ?? null;
        } catch {
            return null;
        }
    };

    const createModerationToken = async (
        galleryId: number,
    ): Promise<ModerationToken | null> => {
        try {
            const response = await authenticatedApi.post(
                `/galleries/moderation/${galleryId}/token`,
            );
            return response.data ?? null;
        } catch {
            return null;
        }
    };

    const revokeModerationToken = async (token: string): Promise<boolean> => {
        try {
            await authenticatedApi.delete(`/galleries/moderation/${token}`);
            return true;
        } catch {
            return false;
        }
    };

    /* gallery intake mode — false also covers "migration pending",
       which renders as the default (appear immediately) */
    const getModerationHold = async (galleryId: number): Promise<boolean> => {
        try {
            const response = await authenticatedApi.get(
                `/galleries/${galleryId}/moderationHold`,
            );
            return response.data?.hold === true;
        } catch {
            return false;
        }
    };

    const setModerationHold = async (
        galleryId: number,
        hold: boolean,
    ): Promise<boolean> => {
        try {
            const response = await authenticatedApi.put(
                `/galleries/${galleryId}/moderationHold`,
                { hold },
            );
            return response.data?.hold === hold;
        } catch {
            return false;
        }
    };

    return {
        getModerationToken,
        createModerationToken,
        revokeModerationToken,
        getModerationHold,
        setModerationHold,
    };
}
